/** XeAgents.ai**/
import { useTranslation } from "react-i18next";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight, Bell, CalendarCheck, HelpCircle, PhoneCall, type LucideIcon } from "lucide-react";
import { scrollToSection } from "@/lib/section-nav";

interface Outcome {
  icon: LucideIcon;
  /** Key under outcomes.* in the landing namespace. */
  key: string;
}

const OUTCOMES: Outcome[] = [
  { icon: CalendarCheck, key: "appointments" },
  { icon: Bell, key: "reminders" },
  { icon: HelpCircle, key: "enquiries" },
  { icon: PhoneCall, key: "followUps" },
];

/**
 * The four workflows a clinic starts with, in the terms an operations manager
 * uses. Each card must be something the product does today.
 */
export function OutcomesSection() {
  const { t } = useTranslation("landing");
  const shouldReduceMotion = useReducedMotion();

  return (
    <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6 sm:py-16 lg:px-8" data-testid="outcomes-section">
      <div className="grid gap-8 lg:grid-cols-2 lg:gap-12">
        <motion.div
          initial={shouldReduceMotion ? { opacity: 1 } : { opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
        >
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-teal-600 dark:text-teal-400">
            {t("outcomes.eyebrow")}
          </p>
          <h2 className="mt-4 text-3xl font-bold leading-tight tracking-tight text-slate-900 dark:text-white sm:text-4xl">
            {t("outcomes.title1")}
            <br />
            {t("outcomes.title2")}
          </h2>
          <p className="mt-4 max-w-lg text-sm leading-relaxed text-slate-600 dark:text-slate-400 sm:text-base">
            {t("outcomes.body")}
          </p>
          <button
            type="button"
            onClick={() => scrollToSection("#use-cases")}
            className="group mt-6 inline-flex items-center gap-2 text-sm font-semibold text-violet-600 transition-colors hover:text-violet-700 dark:text-violet-400 dark:hover:text-violet-300"
            data-testid="button-explore-use-cases"
          >
            {t("hero.seeUseCases")}
            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
          </button>
        </motion.div>

        <div className="grid gap-4 sm:grid-cols-2">
          {OUTCOMES.map(({ icon: Icon, key }, index) => (
            <motion.div
              key={key}
              initial={shouldReduceMotion ? { opacity: 1 } : { opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.45, delay: index * 0.07 }}
              className="rounded-2xl border border-slate-200 bg-white p-5 transition-shadow hover:shadow-md dark:border-slate-800 dark:bg-slate-900"
              data-testid={`outcome-card-${index}`}
            >
              <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-teal-50 dark:bg-teal-950/60">
                <Icon className="h-5 w-5 text-teal-600 dark:text-teal-400" />
              </span>
              <h3 className="mt-3 text-sm font-semibold text-slate-900 dark:text-white">
                {t(`outcomes.${key}.title`)}
              </h3>
              <p className="mt-1 text-xs leading-relaxed text-slate-500 dark:text-slate-400">
                {t(`outcomes.${key}.description`)}
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default OutcomesSection;
